import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import ThermalBillPrint from '../components/ThermalBillPrint';
import { XCircle, AlertTriangle, X, Edit2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/pos`;

// Bill Cancellation Modal
const CancelBillModal = ({ bill, onClose, onConfirm, loading }) => {
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (reason.trim().length < 3) {
      alert('Please provide a valid reason (at least 3 characters)');
      return;
    }
    onConfirm(bill.id, reason, notes);
  };

  // Check if bill has credit payment
  const hasCreditPayment = bill?.payment_modes?.some(pm => pm.mode === 'credit');
  const creditAmount = bill?.payment_modes?.filter(pm => pm.mode === 'credit')
    .reduce((sum, pm) => sum + (pm.amount || 0), 0) || 0;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
        <div className="p-6 border-b border-slate-200 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-50 rounded-lg">
              <XCircle className="text-red-600" size={24} />
            </div>
            <h3 className="text-xl font-semibold">Cancel Bill</h3>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg">
            <X size={20} />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Bill Info */}
          <div className="p-4 bg-slate-50 border border-slate-200 rounded-lg">
            <div className="flex justify-between mb-2">
              <span className="text-sm text-slate-500">Bill Number</span>
              <span className="font-semibold text-violet-600">{bill?.bill_number}</span>
            </div>
            <div className="flex justify-between mb-2">
              <span className="text-sm text-slate-500">Customer</span>
              <span className="font-medium">{bill?.customer_name || 'Walk-in'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-slate-500">Amount</span>
              <span className="font-bold text-lg">₹{(bill?.grand_total || 0).toFixed(2)}</span>
            </div>
          </div>

          {/* Warning for credit bills */}
          {hasCreditPayment && (
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-start gap-2">
                <AlertTriangle className="text-amber-600 mt-0.5" size={18} />
                <div>
                  <p className="font-medium text-amber-800">Credit Sale Warning</p>
                  <p className="text-sm text-amber-700 mt-1">
                    This bill has ₹{creditAmount.toFixed(2)} credit. Cancelling will create a contra entry in customer ledger to reverse the credit.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Reason Input */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Reason for Cancellation <span className="text-red-500">*</span>
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              placeholder="Enter reason for cancellation..."
              className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 resize-none"
              required
              minLength={3}
            />
          </div>

          {/* Notes Input */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Additional Notes (Optional)
            </label>
            <input
              type="text"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Any additional notes..."
              className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-500"
            />
          </div>

          {/* Info */}
          <div className="text-xs text-slate-500 bg-slate-50 p-3 rounded-lg">
            <p>• Stock will be restored to inventory</p>
            <p>• Customer ledger will be updated (if credit sale)</p>
            <p>• This action cannot be undone</p>
          </div>

          {/* Buttons */}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-slate-200 rounded-lg hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || reason.trim().length < 3}
              className="flex-1 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Cancelling...' : 'Confirm Cancellation'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const BillsHistory = () => {
  const navigate = useNavigate();
  const [bills, setBills] = useState([]);
  const [cancelledBills, setCancelledBills] = useState([]);
  const [estimations, setEstimations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('bills'); // bills, cancelled, or estimations
  const [selectedBill, setSelectedBill] = useState(null);
  const [businessInfo, setBusinessInfo] = useState(null);
  const [billSettings, setBillSettings] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [isOwner, setIsOwner] = useState(false);
  
  // Cancel bill state
  const [cancelModal, setCancelModal] = useState({ show: false, bill: null });
  const [cancelling, setCancelling] = useState(false);

  // Filters
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [customerFilter, setCustomerFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    // Fetch both bills and estimations on mount for accurate counts
    fetchAllData();
    fetchBusinessInfo();
    fetchBillSettings();
    fetchCustomers();
    checkOwnerStatus();
    // Set default date filters to yesterday and today (local timezone)
    const localYMD = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    setFromDate(localYMD(yesterday));
    setToDate(localYMD(today));
  }, []);

  const checkOwnerStatus = async () => {
    try {
      const token = localStorage.getItem('pos_token');
      const response = await axios.get(`${API}/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Owner has type 'owner', cashier has type 'cashier'
      setIsOwner(response.data?.type === 'owner' || !response.data?.type);
    } catch (error) {
      console.error('Error checking owner status:', error);
    }
  };

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('pos_token');
      const headers = { Authorization: `Bearer ${token}` };
      
      // Fetch bills, cancelled bills, and estimations in parallel
      const [billsRes, cancelledRes, estimationsRes] = await Promise.all([
        axios.get(`${API}/bills?status=completed&limit=500`, { headers }),
        axios.get(`${API}/bills?status=cancelled&limit=500`, { headers }),
        axios.get(`${API}/estimations?limit=500`, { headers })
      ]);
      
      // Filter out cancelled from regular bills (in case API doesn't filter)
      const activeBills = billsRes.data.filter(b => b.status !== 'cancelled');
      setBills(activeBills);
      setCancelledBills(cancelledRes.data);
      setEstimations(estimationsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelBill = async (billId, reason, notes) => {
    setCancelling(true);
    try {
      const token = localStorage.getItem('pos_token');
      await axios.post(`${API}/bills/${billId}/cancel`, 
        { reason, notes },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setCancelModal({ show: false, bill: null });
      fetchAllData(); // Refresh data
      alert('Bill cancelled successfully. Stock has been restored.');
    } catch (error) {
      console.error('Error cancelling bill:', error);
      alert(error.response?.data?.detail || 'Failed to cancel bill');
    } finally {
      setCancelling(false);
    }
  };

  const fetchBusinessInfo = async () => {
    try {
      const token = localStorage.getItem('pos_token');
      const response = await axios.get(`${API}/settings/business`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBusinessInfo(response.data);
    } catch (error) {
      console.error('Error fetching business info:', error);
    }
  };

  const fetchBillSettings = async () => {
    try {
      const token = localStorage.getItem('pos_token');
      const response = await axios.get(`${API}/settings/bill`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBillSettings(response.data);
    } catch (error) {
      console.error('Error fetching bill settings:', error);
    }
  };

  const fetchCustomers = async () => {
    try {
      const token = localStorage.getItem('pos_token');
      const response = await axios.get(`${API}/customers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCustomers(response.data);
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  };

  const formatCurrency = (amount) => `₹${(amount || 0).toFixed(2)}`;
  // Local (device timezone) YYYY-MM-DD — avoids UTC day-shift that broke single-date filtering
  const toLocalYMD = (dateStr) => {
    const d = new Date(dateStr);
    if (isNaN(d)) return '';
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };
  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
  };

  // Filter data
  const getFilteredData = () => {
    let data;
    if (activeTab === 'bills') {
      data = bills;
    } else if (activeTab === 'cancelled') {
      data = cancelledBills;
    } else {
      data = estimations;
    }
    
    // Date filter (compare in local timezone so single/same-date filtering works)
    if (fromDate) {
      data = data.filter(item => {
        const itemDate = toLocalYMD(item.created_at);
        return itemDate && itemDate >= fromDate;
      });
    }
    if (toDate) {
      data = data.filter(item => {
        const itemDate = toLocalYMD(item.created_at);
        return itemDate && itemDate <= toDate;
      });
    }
    
    // Customer filter
    if (customerFilter) {
      data = data.filter(item => item.customer_id === customerFilter || item.customer_name?.toLowerCase().includes(customerFilter.toLowerCase()));
    }
    
    // Search query (bill/estimation number)
    if (searchQuery) {
      data = data.filter(item => 
        (item.bill_number?.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (item.estimation_number?.toLowerCase().includes(searchQuery.toLowerCase()))
      );
    }
    
    return data;
  };

  const filteredData = getFilteredData();

  // Calculate summary
  const summary = {
    count: filteredData.length,
    total: filteredData.reduce((sum, item) => sum + (item.total || item.grand_total || 0), 0)
  };

  const clearFilters = () => {
    setFromDate('');
    setToDate('');
    setCustomerFilter('');
    setSearchQuery('');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-600"></div>
      </div>
    );
  }

  return (
    <div data-testid="bills-history-page" className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Bills & Estimations</h1>
          <p className="text-slate-500">View, print, download and share your bills and estimations</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 flex-wrap">
        <button
          onClick={() => setActiveTab('bills')}
          className={`px-6 py-2.5 rounded-xl font-medium transition-colors ${
            activeTab === 'bills' 
              ? 'bg-violet-600 text-white' 
              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
          }`}
          data-testid="tab-bills"
        >
          Bills ({bills.length})
        </button>
        <button
          onClick={() => setActiveTab('cancelled')}
          className={`px-6 py-2.5 rounded-xl font-medium transition-colors ${
            activeTab === 'cancelled' 
              ? 'bg-red-600 text-white' 
              : 'bg-red-50 text-red-600 hover:bg-red-100'
          }`}
          data-testid="tab-cancelled"
        >
          Cancelled ({cancelledBills.length})
        </button>
        <button
          onClick={() => setActiveTab('estimations')}
          className={`px-6 py-2.5 rounded-xl font-medium transition-colors ${
            activeTab === 'estimations' 
              ? 'bg-violet-600 text-white' 
              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
          }`}
          data-testid="tab-estimations"
        >
          Estimations ({estimations.length})
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">From Date</label>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
              data-testid="filter-from-date"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">To Date</label>
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
              data-testid="filter-to-date"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Customer</label>
            <select
              value={customerFilter}
              onChange={(e) => setCustomerFilter(e.target.value)}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
              data-testid="filter-customer"
            >
              <option value="">All Customers</option>
              {customers.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Search</label>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Bill/Est number..."
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
              data-testid="filter-search"
            />
          </div>
          <div className="flex items-end">
            <button
              onClick={clearFilters}
              className="w-full px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg text-sm font-medium"
              data-testid="clear-filters-btn"
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {/* Summary */}
      <div className="flex gap-4">
        <div className="bg-violet-50 border border-violet-200 rounded-xl px-4 py-2">
          <span className="text-sm text-violet-600">Showing: </span>
          <span className="font-bold text-violet-700">{summary.count} {activeTab}</span>
        </div>
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-2">
          <span className="text-sm text-emerald-600">Total Value: </span>
          <span className="font-bold text-emerald-700">{formatCurrency(summary.total)}</span>
        </div>
      </div>

      {/* Data Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className={activeTab === 'cancelled' ? 'bg-red-50' : 'bg-slate-50'}>
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">
                  {activeTab === 'estimations' ? 'Estimation No.' : 'Bill No.'}
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Date & Time</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Customer</th>
                <th className="px-4 py-3 text-right text-sm font-medium text-slate-600">Items</th>
                <th className="px-4 py-3 text-right text-sm font-medium text-slate-600">Total</th>
                {activeTab === 'cancelled' && (
                  <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Cancellation Reason</th>
                )}
                <th className="px-4 py-3 text-center text-sm font-medium text-slate-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredData.length === 0 ? (
                <tr>
                  <td colSpan={activeTab === 'cancelled' ? 7 : 6} className="px-4 py-8 text-center text-slate-500">
                    No {activeTab} found with the selected filters
                  </td>
                </tr>
              ) : (
                filteredData.map(item => (
                  <tr key={item.id} className={`hover:bg-slate-50 ${activeTab === 'cancelled' ? 'bg-red-50/30' : ''}`} data-testid={`row-${item.id}`}>
                    <td className="px-4 py-3">
                      <span className={`font-medium ${activeTab === 'cancelled' ? 'text-red-600 line-through' : 'text-violet-600'}`}>
                        {item.bill_number || item.estimation_number}
                      </span>
                      {activeTab === 'cancelled' && (
                        <span className="ml-2 px-2 py-0.5 text-xs bg-red-100 text-red-700 rounded-full">Cancelled</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600">
                      <div>{formatDate(item.created_at)}</div>
                      <div className="text-xs text-slate-400">{formatTime(item.created_at)}</div>
                      {activeTab === 'cancelled' && item.cancelled_at && (
                        <div className="text-xs text-red-500 mt-1">
                          Cancelled: {formatDate(item.cancelled_at)}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-sm font-medium text-slate-800">{item.customer_name || 'Walk-in'}</div>
                      {item.customer_mobile && (
                        <div className="text-xs text-slate-500">{item.customer_mobile}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right text-sm text-slate-600">
                      {item.items?.length || 0}
                    </td>
                    <td className="px-4 py-3 text-right font-semibold text-slate-800">
                      {formatCurrency(item.total || item.grand_total)}
                    </td>
                    {activeTab === 'cancelled' && (
                      <td className="px-4 py-3 text-sm text-slate-600 max-w-xs">
                        <div className="truncate" title={item.cancellation_reason}>
                          {item.cancellation_reason || '-'}
                        </div>
                        {item.cancellation_notes && (
                          <div className="text-xs text-slate-400 truncate" title={item.cancellation_notes}>
                            Note: {item.cancellation_notes}
                          </div>
                        )}
                      </td>
                    )}
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-center gap-1">
                        <button
                          onClick={() => setSelectedBill({ ...item, is_estimation: activeTab === 'estimations' })}
                          className="p-2 text-violet-600 hover:bg-violet-50 rounded-lg transition-colors"
                          title="View & Print"
                          data-testid={`view-${item.id}`}
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                          </svg>
                        </button>
                        {/* Edit button - only if allow_bill_edit is enabled, active bills/estimations, owner only */}
                        {activeTab !== 'cancelled' && isOwner && billSettings?.allow_bill_edit && (
                          <button
                            onClick={() => navigate(`/gposp/pos?edit=${item.id}${activeTab === 'estimations' ? '&type=estimation' : ''}`)}
                            className="p-2 text-amber-600 hover:bg-amber-50 rounded-lg transition-colors"
                            title="Edit Bill"
                            data-testid={`edit-${item.id}`}
                          >
                            <Edit2 size={20} />
                          </button>
                        )}
                        {/* Cancel button - only for active bills and owner only */}
                        {activeTab === 'bills' && isOwner && (
                          <button
                            onClick={() => setCancelModal({ show: true, bill: item })}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Cancel Bill"
                            data-testid={`cancel-${item.id}`}
                          >
                            <XCircle size={20} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Print Modal */}
      {selectedBill && (
        <ThermalBillPrint
          bill={selectedBill}
          businessInfo={businessInfo}
          billSettings={billSettings}
          onClose={() => setSelectedBill(null)}
        />
      )}

      {/* Cancel Bill Modal */}
      {cancelModal.show && (
        <CancelBillModal
          bill={cancelModal.bill}
          onClose={() => setCancelModal({ show: false, bill: null })}
          onConfirm={handleCancelBill}
          loading={cancelling}
        />
      )}
    </div>
  );
};

export default BillsHistory;
